/**
 * Persistent on-screen card shown while a rescue tug is travelling to
 * the player. Mounts near the top of the screen so it doesn't clash
 * with the bottom-centre stat bars or the side minimap.
 *
 *   - title         "RECOVERY TUG EN ROUTE"
 *   - subtitle      flavor line that swaps to "Closing in" near arrival
 *   - eta value     `ETA · 24s`
 *   - progress bar  visualises closing distance
 *
 * The HUD is driven imperatively by the GameDirector via `setStatus()`
 * — it doesn't sample the service, so other status messages (warp,
 * patrol alerts) can reuse the same surface in the future.
 */
export class RescueStatusHud {
  readonly element: HTMLElement;
  private readonly subtitleEl: HTMLElement;
  private readonly etaEl: HTMLElement;
  private readonly fillEl: HTMLElement;
  private visible = false;
  private maxEta = 0;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'rescue-status';

    const head = document.createElement('div');
    head.className = 'rescue-status-head';

    const icon = document.createElement('div');
    icon.className = 'rescue-status-icon';
    icon.innerHTML = TUG_ICON;

    const text = document.createElement('div');
    text.className = 'rescue-status-text';
    const title = document.createElement('div');
    title.className = 'rescue-status-title';
    title.textContent = 'Recovery tug en route';
    this.subtitleEl = document.createElement('div');
    this.subtitleEl.className = 'rescue-status-sub';
    this.subtitleEl.textContent = 'Hold position. Tug dispatched from the station.';
    text.append(title, this.subtitleEl);

    this.etaEl = document.createElement('div');
    this.etaEl.className = 'rescue-status-eta';
    this.etaEl.innerHTML =
      '<span class="rescue-status-eta-label">ETA</span><span class="rescue-status-eta-value">—</span>';

    head.append(icon, text, this.etaEl);

    const bar = document.createElement('div');
    bar.className = 'rescue-status-bar';
    this.fillEl = document.createElement('div');
    this.fillEl.className = 'rescue-status-bar-fill';
    bar.appendChild(this.fillEl);

    this.element.append(head, bar);
  }

  /** Show the HUD with a given ETA in seconds. Call once per frame
   *  while the tug is en route. The card auto-fades in on the first
   *  call after a hide and animates the bar based on remaining time. */
  setStatus(etaSeconds: number): void {
    if (!this.visible) {
      this.visible = true;
      this.element.classList.add('is-visible');
      // Capture the ETA at the moment the HUD opens — used to scale the
      // progress bar so it grows smoothly from 0 to 100% as the tug
      // closes in. Bumps if the ETA briefly grows (player drifting).
      this.maxEta = Math.max(etaSeconds, 1);
    }
    if (etaSeconds > this.maxEta) this.maxEta = etaSeconds;

    const display = Math.max(0, Math.ceil(etaSeconds));
    const valueEl = this.etaEl.querySelector(
      '.rescue-status-eta-value'
    ) as HTMLElement | null;
    if (valueEl && valueEl.textContent !== `${display}s`) {
      valueEl.textContent = `${display}s`;
    }

    const progress =
      this.maxEta > 0
        ? Math.min(1, Math.max(0, 1 - etaSeconds / this.maxEta))
        : 0;
    const widthStr = `${(progress * 100).toFixed(1)}%`;
    if (this.fillEl.dataset['lastWidth'] !== widthStr) {
      this.fillEl.style.width = widthStr;
      this.fillEl.dataset['lastWidth'] = widthStr;
    }

    if (etaSeconds < 6) {
      this.subtitleEl.textContent = 'Closing in. Stay on station.';
    } else {
      this.subtitleEl.textContent = 'Hold position. Tug dispatched from the station.';
    }
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.element.classList.remove('is-visible');
    this.maxEta = 0;
    this.fillEl.dataset['lastWidth'] = '';
    this.fillEl.style.width = '0%';
  }

  isVisible(): boolean {
    return this.visible;
  }
}

const TUG_ICON = `
<svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <rect x="6" y="11" width="16" height="10" rx="2" stroke="currentColor" stroke-width="2" />
  <path d="M2 14h4M2 17h4M22 13l5 0M22 19l5 0" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
  <circle cx="11" cy="16" r="1.4" fill="currentColor" />
  <circle cx="17" cy="16" r="1.4" fill="currentColor" />
  <path d="M27 13v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
</svg>`;
